import React, { useState, useEffect, useRef } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../ui/dialog";
import { Button } from "../../ui/button";
import { Label } from "../../ui/label";
import { Loader2, Plus, Trash2, Paperclip, X, FileText } from "lucide-react";
import { toast } from "sonner";
import { processService } from "../../../services/process.service";
import { documentTypeService } from "../../../services/document_type.service";
import cedenteDocumentoService from "../../../services/cedenteDocumento.service";
import { useProcess } from "../../../contexts/ProcessContext";

const TIPO_COLORS = {
  termo:      "bg-statusInfoBg text-statusInfo border-statusInfo/20",
  cessao:     "bg-violet-50 text-violet-700 border-violet-200",
  anuencia:   "bg-purple-50 text-purple-700 border-purple-200",
  honorarios: "bg-statusWarningBg text-statusWarning border-statusWarning/20",
  outro:      "bg-muted text-muted-foreground border-border",
};

function todayBR() {
  const d = new Date();
  return [
    String(d.getDate()).padStart(2, "0"),
    String(d.getMonth() + 1).padStart(2, "0"),
    String(d.getFullYear()),
  ].join("/");
}

function parseBRDate(str) {
  const [d, m, y] = (str || "").split("/");
  if (!d || !m || !y || y.length < 4) return null;
  const date = new Date(`${y}-${m}-${d}T12:00:00`);
  return isNaN(date.getTime()) ? null : date.toISOString();
}

function maskDate(raw) {
  const digits = raw.replace(/\D/g, "").slice(0, 8);
  if (digits.length <= 2) return digits;
  if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
}

function emptyEntry() {
  return { tipo_documento: "termo", processo_documento_id: null, data_assinatura: todayBR(), observacao: "", files: [] };
}

function FileAttach({ files, onAdd, onRemove }) {
  const ref = useRef();
  return (
    <div className="space-y-1.5">
      <div className="flex flex-wrap gap-1.5">
        {files.map((f, i) => (
          <div key={i} className="flex items-center gap-1.5 px-2 py-1 rounded-md border border-border bg-muted text-xs text-foreground max-w-full overflow-hidden">
            <Paperclip className="h-3 w-3 shrink-0 text-muted-foreground" />
            <span className="truncate max-w-[140px]">{f.name}</span>
            <button type="button" onClick={() => onRemove(i)} className="ml-1 text-muted-foreground hover:text-red-400 shrink-0">
              <X className="h-3 w-3" />
            </button>
          </div>
        ))}
        <button
          type="button"
          onClick={() => ref.current?.click()}
          className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground border border-dashed border-gray-300 rounded-md px-2 py-1 transition-colors"
        >
          <Paperclip className="h-3 w-3" />
          {files.length === 0 ? <>Anexar documento <span className="text-muted-foreground">(opcional)</span></> : "Adicionar mais"}
        </button>
      </div>
      <input
        ref={ref}
        type="file"
        accept=".pdf,.png,.jpg,.jpeg"
        multiple
        className="hidden"
        onChange={(e) => { onAdd(Array.from(e.target.files)); e.target.value = ""; }}
      />
    </div>
  );
}

export function AssinaturaManualDialog({ isOpen, onOpenChange, processoId, cedente, onSuccess }) {
  const [entries, setEntries] = useState([emptyEntry()]);
  const [saving, setSaving] = useState(false);
  const [docTypes, setDocTypes] = useState([]);
  const { processo } = useProcess();

  const processoDocs = (processo?.documentos ?? []).filter(d => !d.deleted_at && !d.parent_id);

  React.useEffect(() => {
    if (isOpen) setEntries([emptyEntry()]);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    documentTypeService.listAll()
      .then(types => setDocTypes(types.filter(t => t.is_active)))
      .catch(e => toast.error(e?.response?.data?.detail || "Erro ao carregar tipos de documento."));
  }, [isOpen]);

  const addEntry = () => setEntries(prev => [...prev, emptyEntry()]);
  const removeEntry = (idx) => setEntries(prev => prev.filter((_, i) => i !== idx));
  const updateEntry = (idx, field, value) =>
    setEntries(prev => prev.map((e, i) => i === idx ? { ...e, [field]: value } : e));

  const selectDoc = (idx, doc) => {
    setEntries(prev => prev.map((e, i) =>
      i === idx
        ? { ...e, processo_documento_id: doc.id, tipo_documento: doc.tipo }
        : e
    ));
  };

  const handleSubmit = async () => {
    if (!cedente) return;
    setSaving(true);
    let ok = 0;
    const errors = [];

    for (const entry of entries) {
      const isoDate = parseBRDate(entry.data_assinatura);
      if (!isoDate) {
        errors.push(`Data inválida para '${docTypes.find(t => t.slug === entry.tipo_documento)?.label ?? entry.tipo_documento}'`);
        continue;
      }

      try {
        const cedente_documento_ids = [];
        for (const file of entry.files) {
          const doc = await cedenteDocumentoService.uploadDocumento(
            cedente.id,
            "assinatura_manual",
            entry.tipo_documento,
            file,
          );
          cedente_documento_ids.push(doc.id);
        }

        await processService.registerManualSignature(processoId, {
          cedente_id: cedente.id,
          tipo_documento: entry.tipo_documento,
          processo_documento_id: entry.processo_documento_id ?? undefined,
          data_assinatura: isoDate,
          observacao: entry.observacao || undefined,
          cedente_documento_ids,
        });
        ok++;
      } catch (err) {
        const msg = err?.response?.data?.detail || `Erro em '${docTypes.find(t => t.slug === entry.tipo_documento)?.label ?? entry.tipo_documento}'`;
        errors.push(msg);
      }
    }

    setSaving(false);
    if (ok > 0) {
      toast.success(`${ok} assinatura${ok > 1 ? "s" : ""} manual${ok > 1 ? "is" : ""} registrada${ok > 1 ? "s" : ""}.`);
      onSuccess?.();
    }
    errors.forEach(e => toast.error(e));
    if (errors.length === 0) onOpenChange(false);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Registrar Assinatura Manual</DialogTitle>
        </DialogHeader>

        {cedente && (
          <p className="text-sm text-muted-foreground mb-1">
            <span className="font-medium text-foreground">{cedente.nome}</span>
            {cedente.cpf && <span className="ml-2 text-xs">({cedente.cpf})</span>}
          </p>
        )}

        <div className="space-y-3 py-1 max-h-[55vh] overflow-y-auto pr-1">
          {entries.map((entry, idx) => {
            const selectedDoc = processoDocs.find(d => d.id === entry.processo_documento_id);
            return (
              <div key={idx} className="rounded-lg border border-border p-3 space-y-3 relative">
                {entries.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeEntry(idx)}
                    className="absolute top-2 right-2 text-gray-300 hover:text-red-400 transition-colors"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                )}

                {processoDocs.length > 0 && (
                  <div className="space-y-1.5">
                    <Label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                      Documento do Processo <span className="text-muted-foreground font-normal normal-case">(opcional)</span>
                    </Label>
                    <div className="flex flex-wrap gap-1.5">
                      {processoDocs.map(doc => (
                        <button
                          key={doc.id}
                          type="button"
                          onClick={() => selectDoc(idx, doc)}
                          className={`flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium border transition-all ${
                            entry.processo_documento_id === doc.id
                              ? (TIPO_COLORS[doc.tipo] ?? "bg-muted text-muted-foreground border-border") + " ring-2 ring-offset-1"
                              : "bg-card text-muted-foreground border-border hover:border-gray-300 hover:text-muted-foreground"
                          }`}
                        >
                          <FileText className="h-3 w-3 shrink-0" />
                          {docTypes.find(t => t.slug === doc.tipo)?.label ?? doc.tipo}
                          {doc.etapa > 1 && <span className="opacity-60 ml-0.5">· etapa {doc.etapa}</span>}
                        </button>
                      ))}
                      {entry.processo_documento_id && (
                        <button
                          type="button"
                          onClick={() => updateEntry(idx, "processo_documento_id", null)}
                          className="px-2 py-1 rounded-md text-xs text-muted-foreground hover:text-muted-foreground border border-dashed border-border transition-colors"
                        >
                          Limpar
                        </button>
                      )}
                    </div>
                  </div>
                )}

                <div className="space-y-1">
                  <Label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                    Tipo de Documento
                  </Label>
                  <div className="flex flex-wrap gap-1.5">
                    {docTypes.map(t => (
                      <button
                        key={t.slug}
                        type="button"
                        onClick={() => updateEntry(idx, "tipo_documento", t.slug)}
                        className={`px-2.5 py-1 rounded-md text-xs font-medium border transition-all ${
                          entry.tipo_documento === t.slug
                            ? (TIPO_COLORS[t.slug] ?? "bg-muted text-muted-foreground border-border") + " ring-2 ring-offset-1"
                            : "bg-card text-muted-foreground border-border hover:border-gray-300"
                        }`}
                      >
                        {t.label}
                      </button>
                    ))}
                  </div>
                  {selectedDoc && entry.tipo_documento !== selectedDoc.tipo && (
                    <p className="text-xs text-amber-600 mt-1">
                      O tipo foi alterado manualmente e difere do documento selecionado.
                    </p>
                  )}
                </div>

                <div className="space-y-1">
                  <Label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                    Data da Assinatura
                  </Label>
                  <input
                    type="text"
                    inputMode="numeric"
                    placeholder="dd/mm/aaaa"
                    maxLength={10}
                    value={entry.data_assinatura}
                    onChange={(e) => updateEntry(idx, "data_assinatura", maskDate(e.target.value))}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                  />
                </div>

                <div className="space-y-1">
                  <Label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                    Observação <span className="text-muted-foreground font-normal normal-case text-xs">(opcional)</span>
                  </Label>
                  <textarea
                    className="flex min-h-[56px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                    placeholder="Ex: assinou fisicamente em audiência"
                    value={entry.observacao}
                    onChange={(e) => updateEntry(idx, "observacao", e.target.value)}
                  />
                </div>

                <FileAttach
                  files={entry.files}
                  onAdd={(newFiles) => updateEntry(idx, "files", [...entry.files, ...newFiles])}
                  onRemove={(i) => updateEntry(idx, "files", entry.files.filter((_, fi) => fi !== i))}
                />
              </div>
            );
          })}
        </div>

        <button
          type="button"
          onClick={addEntry}
          className="flex items-center gap-1.5 text-sm text-amber-600 hover:text-statusWarning transition-colors mt-1"
        >
          <Plus className="h-3.5 w-3.5" />
          Adicionar outro documento
        </button>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancelar
          </Button>
          <Button onClick={handleSubmit} disabled={saving} className="bg-amber-600 hover:bg-amber-700 text-white">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
            Confirmar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
